/**
 * AI Tokens Consumer Portal — Authentication Service (Cognito)
 *
 * Handles consumer authentication via Amazon Cognito User Pool.
 *
 * Auth flow:
 *   1. Consumer signs up with email + password → UNCONFIRMED
 *   2. Admin confirms the user → CONFIRMED
 *   3. Consumer signs in → Cognito returns ID + Access + Refresh tokens
 *   4. ID token is used as Bearer token for API calls
 *   5. Refresh token silently renews expired tokens
 *
 * Self-sign-up is enabled. Admin confirmation is required before first sign-in.
 * When an unconfirmed user tries to sign in, Cognito throws UserNotConfirmedException.
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import { appConfig } from '../config';
import { setTokenProvider } from './api.client';
import type { User, LoginCredentials, RegisterData, ApiResponse } from '../types';

// ── Cognito Configuration ────────────────────────────────────────

const userPool = new CognitoUserPool({
  UserPoolId: appConfig.cognitoUserPoolId,
  ClientId: appConfig.cognitoClientId,
});

/** Currently authenticated Cognito user */
let currentUser: CognitoUser | null = null;

/** Current session (contains JWT tokens) */
let currentSession: CognitoUserSession | null = null;

// Register token provider so api.client attaches the Bearer token
setTokenProvider(() => {
  if (!currentSession || !currentSession.isValid()) return null;
  return currentSession.getIdToken().getJwtToken();
});

// ── Helpers ──────────────────────────────────────────────────────

/** Build a User object from a Cognito JWT payload. */
function userFromPayload(
  payload: Record<string, string>
): User {
  return {
    id: payload.sub || '',
    email: payload.email || '',
    name: payload.name || (payload.email || '').split('@')[0],
    createdAt: new Date().toISOString(),
    avatarUrl: undefined,
  };
}

// ── Sign Up ──────────────────────────────────────────────────────

/**
 * Register a new consumer account.
 * User will be UNCONFIRMED until an admin approves them.
 */
export async function register(
  data: RegisterData
): Promise<ApiResponse<User>> {
  return new Promise((resolve) => {
    const attributeList = [
      new CognitoUserAttribute({ Name: 'email', Value: data.email }),
      new CognitoUserAttribute({ Name: 'name', Value: data.name }),
    ];

    userPool.signUp(
      data.email,
      data.password,
      attributeList,
      [],
      (err) => {
        if (err) {
          const code = (err as unknown as Record<string, string>).code;

          if (code === 'UsernameExistsException') {
            resolve({
              success: false,
              error: 'An account with this email already exists.',
            });
            return;
          }
          if (code === 'InvalidPasswordException') {
            resolve({
              success: false,
              error: err.message || 'Password does not meet requirements.',
            });
            return;
          }
          resolve({
            success: false,
            error: err.message || 'Registration failed. Please try again.',
          });
          return;
        }

        // Sign-up succeeded — user is UNCONFIRMED, needs admin approval
        resolve({
          success: true,
          message: 'Account created! An administrator will review and approve your account.',
        });
      }
    );
  });
}

// ── Sign In ──────────────────────────────────────────────────────

/**
 * Authenticate a consumer user with email and password.
 *
 * Returns:
 *   - success + User on valid credentials with confirmed account
 *   - error with `needsConfirmation` flag when account is pending approval
 *   - error on bad credentials (generic message, no user-existence leakage)
 */
export async function login(
  credentials: LoginCredentials
): Promise<ApiResponse<User> & { needsConfirmation?: boolean; needsNewPassword?: boolean }> {
  return new Promise((resolve) => {
    const cognitoUser = new CognitoUser({
      Username: credentials.email,
      Pool: userPool,
    });

    const authDetails = new AuthenticationDetails({
      Username: credentials.email,
      Password: credentials.password,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session: CognitoUserSession) => {
        currentUser = cognitoUser;
        currentSession = session;

        const payload = session.getIdToken().decodePayload();
        resolve({
          success: true,
          data: userFromPayload(payload),
        });
      },

      onFailure: (err: Error) => {
        const code = (err as unknown as Record<string, string>).code;

        if (code === 'UserNotConfirmedException') {
          resolve({
            success: false,
            needsConfirmation: true,
            error:
              'Your account is pending approval. Please wait for an administrator to confirm your account.',
          });
          return;
        }

        // NotAuthorizedException or UserNotFoundException → same message
        if (
          code === 'NotAuthorizedException' ||
          code === 'UserNotFoundException'
        ) {
          resolve({
            success: false,
            error: 'Incorrect email or password.',
          });
          return;
        }

        resolve({
          success: false,
          error: err.message || 'Sign-in failed. Please try again.',
        });
      },

      newPasswordRequired: () => {
        // Admin-created accounts need a new password on first login.
        // Store the user reference so completeNewPassword can use it.
        currentUser = cognitoUser;
        resolve({
          success: true,
          needsNewPassword: true,
        });
      },
    });
  });
}

// ── New Password Challenge ────────────────────────────────────────

/**
 * Complete the new-password challenge for admin-created accounts.
 * Called after login resolves with `needsNewPassword: true`.
 */
export function completeNewPassword(
  newPassword: string
): Promise<ApiResponse<User>> {
  return new Promise((resolve) => {
    if (!currentUser) {
      resolve({ success: false, error: 'No active authentication session.' });
      return;
    }

    currentUser.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session: CognitoUserSession) => {
        currentSession = session;

        const payload = session.getIdToken().decodePayload();
        resolve({
          success: true,
          data: userFromPayload(payload),
        });
      },
      onFailure: (err: Error) => {
        const code = (err as unknown as Record<string, string>).code;

        if (code === 'InvalidPasswordException') {
          resolve({
            success: false,
            error: err.message || 'Password does not meet requirements.',
          });
          return;
        }

        resolve({
          success: false,
          error: err.message || 'Failed to set new password. Please try again.',
        });
      },
    });
  });
}

// ── Session Management ───────────────────────────────────────────

/**
 * Restore an existing session from Cognito's local storage.
 * Called on app mount to resume authenticated sessions across refreshes.
 */
export async function restoreSession(): Promise<ApiResponse<User>> {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      resolve({ success: false, error: 'No session' });
      return;
    }

    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve({ success: false, error: 'Session expired' });
          return;
        }

        currentUser = cognitoUser;
        currentSession = session;

        const payload = session.getIdToken().decodePayload();
        resolve({
          success: true,
          data: userFromPayload(payload),
        });
      }
    );
  });
}

/**
 * Sign out the current user.
 * Clears Cognito tokens from local storage.
 */
export async function logout(): Promise<void> {
  if (currentUser) {
    currentUser.signOut();
  }
  currentUser = null;
  currentSession = null;
}

/**
 * Check if there is a valid session.
 */
export function isAuthenticated(): boolean {
  return !!currentSession && currentSession.isValid();
}

/**
 * Check current session — backward-compat adapter.
 */
export async function getCurrentUser(): Promise<ApiResponse<User>> {
  return restoreSession();
}
